/**
 * PATH: packages/ui/src/components/sidebar/HiddenNavTray.tsx
 * FILE_ID: UI-NAV-HIDDEN-TRAY-001
 * PURPOSE: Collapsible "Hidden (N)" tray at the bottom of the shared
 *          Sidebar that lists nav items the user hid (right-click → "Hide
 *          from sidebar") and lets them restore each one.
 * WHY: Operator: "right click on jobs or calendar and hide / pin it for
 *      menu building". Hiding must be REVERSIBLE — without a restore
 *      surface a hidden item would be lost forever. This tray is that
 *      surface (the chosen "Hidden (N) restore tray" design).
 * FLOW:
 *   ┌──────────────┐   ┌───────────────┐   ┌──────────────────────┐
 *   │ hiddenItems  │ → │ collapsible   │ → │ click item → onUnhide│
 *   │ (from hook)  │   │ "Hidden (N)"  │   │ → item back in rail  │
 *   └──────────────┘   └───────────────┘   └──────────────────────┘
 * RELATED:
 *   - ./index.tsx (renders this at the foot of the nav)
 *   - ../../hooks/useNavCustomization.ts (owns hiddenItems + unhideItem)
 */

import { useState } from 'react'
import { ChevronDown, EyeOff, RotateCcw } from 'lucide-react'
import { cn } from '../../lib/utils'
import type { NavItem } from '../types'

export interface HiddenNavTrayProps {
  hiddenItems: NavItem[]
  onUnhide: (itemId: string) => void
  collapsed: boolean
  /** Section caption — defaults to "Hidden". */
  label?: string
}

/**
 * The "Hidden (N)" tray. Renders nothing when there's nothing hidden, so
 * a user who never hides anything sees zero visual diff. In the collapsed
 * rail it shrinks to a single eye-off button that expands the list.
 */
export function HiddenNavTray({
  hiddenItems,
  onUnhide,
  collapsed,
  label = 'Hidden',
}: HiddenNavTrayProps) {
  // Default collapsed: the whole point of hiding is to declutter, so the
  // tray must not re-clutter the rail. The operator opens it only to
  // restore something.
  const [open, setOpen] = useState(false)

  if (!hiddenItems || hiddenItems.length === 0) return null

  return (
    <div className="mt-3 pt-3 border-t border-border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="quotech-hidden-nav-list"
        title={`${hiddenItems.length} hidden — click to restore`}
        className={cn(
          'w-full flex items-center gap-2 rounded-sm px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground transition-colors hover:text-foreground',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500',
          collapsed && 'justify-center',
        )}
      >
        <EyeOff className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
        {!collapsed && (
          <>
            <span className="flex-1 text-left">
              {label} ({hiddenItems.length})
            </span>
            <ChevronDown
              className={cn('h-3 w-3 transition-transform', open && 'rotate-180')}
              aria-hidden="true"
            />
          </>
        )}
      </button>

      {open && (
        <ul id="quotech-hidden-nav-list" className="mt-1 space-y-0.5">
          {hiddenItems.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => onUnhide(item.id)}
                title={`Restore ${item.label} to the sidebar`}
                aria-label={`Restore ${item.label} to the sidebar`}
                className={cn(
                  'group flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-[12px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
                  collapsed && 'justify-center px-0',
                )}
              >
                {item.icon && <item.icon className="h-3.5 w-3.5 flex-shrink-0 opacity-70" />}
                {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
                {!collapsed && (
                  <RotateCcw className="h-3.5 w-3.5 flex-shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default HiddenNavTray
